<!--
 * @Description: 电站巡检配置
-->
<template>
  <scroll-layout class="inspection-config">
    <div class="config-container">
      <!-- 顶部操作栏 -->
      <div class="config-header">
        <div class="config-title">电站巡检配置</div>
        <div class="config-actions">
          <ykc-button
            v-if="!isEdit"
            v-rbac="'operation:inspectionConfig:edit'"
            type="primary"
            @click="handleEdit">
            编辑
          </ykc-button>
          <ykc-button v-else type="plain" @click="handleCancelEdit">查看模式</ykc-button>
        </div>
      </div>

      <!-- 配置内容 -->
      <div class="config-body">
        <!-- 基本配置 -->
        <div class="config-section">
          <div class="section-title">基本配置</div>
          <ykc-form
            ref="configForm"
            :model="formData"
            :rules="formRules"
            :disabled="!isEdit"
            label-width="200px">
            <ykc-form-item label="电站巡检校验距离" prop="inspectionDistance">
              <el-input
                v-model="formData.inspectionDistance"
                placeholder="请输入校验距离"
                type="number"
                style="width: 300px">
                <template slot="append">km</template>
              </el-input>
            </ykc-form-item>
            <ykc-form-item label="常驻城市完成1轮巡检周期" prop="residentCityCycle">
              <el-select
                v-model="formData.residentCityCycle"
                placeholder="请选择"
                style="width: 300px"
                :disabled="!isEdit">
                <el-option
                  v-for="item in cycleOptions"
                  :key="item.value"
                  :label="item.label"
                  :value="item.value"></el-option>
              </el-select>
              <span class="unit-text">月</span>
            </ykc-form-item>
            <ykc-form-item label="非常驻城市完成1轮巡检周期" prop="nonResidentCityCycle">
              <el-select
                v-model="formData.nonResidentCityCycle"
                placeholder="请选择"
                style="width: 300px"
                :disabled="!isEdit">
                <el-option
                  v-for="item in cycleOptions"
                  :key="item.value"
                  :label="item.label"
                  :value="item.value"></el-option>
              </el-select>
              <span class="unit-text">月</span>
            </ykc-form-item>
          </ykc-form>
        </div>

        <!-- 巡检项配置 -->
        <div class="config-section">
          <div class="section-title">
            <span>电站巡检项配置</span>
            <ykc-button v-if="isEdit" type="primary" size="small" @click="addSubject">
              + 新增巡检科目
            </ykc-button>
          </div>

          <el-collapse v-model="activeSubject" accordion>
            <el-collapse-item
              v-for="(subject, sIndex) in formData.subjects"
              :key="sIndex"
              :name="String(sIndex)">
              <template slot="title">
                <div class="subject-header">
                  <span class="subject-label">
                    巡检科目{{ sIndex + 1 }}：{{ subject.subjectName || '未命名' }}
                  </span>
                  <ykc-button
                    v-if="isEdit && formData.subjects.length >= 2"
                    type="text"
                    class="delete-btn"
                    @click.stop="removeSubject(sIndex)">
                    删除科目
                  </ykc-button>
                </div>
              </template>

              <ykc-form
                :ref="'subjectForm_' + sIndex"
                :model="subject"
                :rules="subjectRules"
                :disabled="!isEdit"
                label-width="120px">
                <ykc-form-item
                  :label="'科目名称'"
                  :prop="'subjectName'"
                  :rules="subjectRules.subjectName">
                  <el-input
                    v-model="subject.subjectName"
                    placeholder="结合巡检项类别进行具体划分，如设备外观检查"
                    style="width: 400px"
                    :disabled="!isEdit"></el-input>
                </ykc-form-item>

                <!-- 巡检项列表 -->
                <div class="item-card" v-for="(item, iIndex) in subject.items" :key="iIndex">
                  <div class="item-header">
                    <span class="item-label">巡检项{{ iIndex + 1 }}</span>
                    <ykc-button
                      v-if="isEdit && subject.items.length >= 2"
                      type="text"
                      class="delete-btn"
                      @click="removeItem(sIndex, iIndex)">
                      删除巡检项
                    </ykc-button>
                  </div>

                  <ykc-form-item
                    label="巡检内容"
                    :prop="'items.' + iIndex + '.inspectionContent'"
                    :rules="itemRules.inspectionContent">
                    <el-input
                      v-model="item.inspectionContent"
                      placeholder="简要描述所需巡检的具体内容，如充电线缆是否完好无破损"
                      style="width: 500px"
                      :disabled="!isEdit"></el-input>
                  </ykc-form-item>

                  <ykc-form-item label="巡检标准">
                    <el-input
                      v-model="item.inspectionStandard"
                      placeholder="按需结合巡检内容填写对应的操作规范指引"
                      style="width: 500px"
                      :disabled="!isEdit"></el-input>
                  </ykc-form-item>

                  <ykc-form-item label="操作项配置">
                    <el-checkbox-group v-model="item.operationOptionList" :disabled="!isEdit">
                      <el-checkbox :label="1">合格</el-checkbox>
                      <el-checkbox :label="2">不合格</el-checkbox>
                      <el-checkbox :label="3">不适用</el-checkbox>
                    </el-checkbox-group>
                  </ykc-form-item>

                  <ykc-form-item label="是否需上传专项巡检照">
                    <el-radio-group v-model="item.needUploadPhoto" :disabled="!isEdit">
                      <el-radio :label="1">是</el-radio>
                      <el-radio :label="0">否</el-radio>
                    </el-radio-group>
                  </ykc-form-item>

                  <ykc-form-item label="是否需填写专项巡检记录">
                    <el-radio-group v-model="item.needWriteRecord" :disabled="!isEdit">
                      <el-radio :label="1">是</el-radio>
                      <el-radio :label="0">否</el-radio>
                    </el-radio-group>
                  </ykc-form-item>
                </div>

                <!-- 新增巡检项按钮 -->
                <div class="add-item-btn" v-if="isEdit">
                  <ykc-button type="plain" size="small" @click="addItem(sIndex)">
                    + 新增巡检项
                  </ykc-button>
                </div>
              </ykc-form>
            </el-collapse-item>
          </el-collapse>

          <div v-if="!formData.subjects || formData.subjects.length === 0" class="empty-tip">
            暂无巡检项配置，请点击"新增巡检科目"按钮添加
          </div>
        </div>
      </div>

      <!-- 底部操作按钮（仅编辑状态展示） -->
      <div class="config-footer" v-if="isEdit">
        <ykc-button type="plain" @click="handleCancel">取消</ykc-button>
        <ykc-button type="primary" @click="handleSave">保存</ykc-button>
      </div>
    </div>

    <!-- 取消确认弹窗 -->
    <el-dialog
      title="提示"
      :visible.sync="cancelDialogVisible"
      width="400px"
      :append-to-body="true">
      <span>确认取消编辑？未保存的修改将丢失。</span>
      <span slot="footer" class="dialog-footer">
        <el-button @click="cancelDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="confirmCancel">确认</el-button>
      </span>
    </el-dialog>
  </scroll-layout>
</template>

<script>
  import { inspectionConfigApi } from '@/service/apis';

  export default {
    name: 'InspectionConfig',
    data() {
      return {
        // 是否编辑状态
        isEdit: false,
        // 手风琴当前展开项
        activeSubject: '0',
        // 取消确认弹窗
        cancelDialogVisible: false,
        // 表单数据
        formData: {
          id: null,
          inspectionDistance: '',
          residentCityCycle: '',
          nonResidentCityCycle: '',
          subjects: [],
        },
        // 备份数据（取消时恢复）
        backupData: null,
        // 周期下拉选项
        cycleOptions: [
          { value: 0.5, label: '0.5' },
          { value: 1, label: '1' },
          { value: 2, label: '2' },
          { value: 3, label: '3' },
          { value: 6, label: '6' },
          { value: 12, label: '12' },
        ],
        // 基本配置校验规则
        formRules: {
          inspectionDistance: [
            { required: true, message: '请输入电站巡检校验距离', trigger: 'blur' },
          ],
          residentCityCycle: [
            { required: true, message: '请选择常驻城市巡检周期', trigger: 'change' },
          ],
          nonResidentCityCycle: [
            { required: true, message: '请选择非常驻城市巡检周期', trigger: 'change' },
          ],
        },
        // 科目校验规则
        subjectRules: {
          subjectName: [{ required: true, message: '请输入科目名称', trigger: 'blur' }],
        },
        // 巡检项校验规则
        itemRules: {
          inspectionContent: [{ required: true, message: '请输入巡检内容', trigger: 'blur' }],
        },
      };
    },
    created() {
      this.getConfig();
    },
    methods: {
      /** 获取配置数据 */
      getConfig() {
        inspectionConfigApi.getConfig().then(res => {
          if (res) {
            this.formData = this.normalizeData(res);
          } else {
            // 无配置数据时初始化空结构
            this.formData = {
              id: null,
              inspectionDistance: '',
              residentCityCycle: '',
              nonResidentCityCycle: '',
              subjects: [],
            };
          }
        });
      },
      /** 数据规范化处理 */
      normalizeData(data) {
        const result = JSON.parse(JSON.stringify(data));
        if (result.subjects) {
          result.subjects.forEach(subject => {
            if (subject.items) {
              subject.items.forEach(item => {
                // 将 operationOptions 字符串转为列表
                if (!item.operationOptionList && item.operationOptions) {
                  item.operationOptionList = item.operationOptions
                    .split(',')
                    .map(v => parseInt(v, 10));
                } else if (!item.operationOptionList) {
                  item.operationOptionList = [1, 2];
                }
                // 默认值处理
                if (item.needUploadPhoto === null || item.needUploadPhoto === undefined) {
                  item.needUploadPhoto = 1;
                }
                if (item.needWriteRecord === null || item.needWriteRecord === undefined) {
                  item.needWriteRecord = 1;
                }
              });
            }
          });
        }
        return result;
      },
      /** 切换到编辑状态 */
      handleEdit() {
        this.backupData = JSON.parse(JSON.stringify(this.formData));
        this.isEdit = true;
      },
      /** 切换回查看状态 */
      handleCancelEdit() {
        this.isEdit = false;
        if (this.backupData) {
          this.formData = this.backupData;
        }
      },
      /** 取消按钮（弹出二次确认） */
      handleCancel() {
        this.cancelDialogVisible = true;
      },
      /** 确认取消 */
      confirmCancel() {
        this.cancelDialogVisible = false;
        this.isEdit = false;
        if (this.backupData) {
          this.formData = JSON.parse(JSON.stringify(this.backupData));
        }
      },
      /** 保存 */
      handleSave() {
        // 校验基本配置表单
        this.$refs.configForm.validate(async valid => {
          if (!valid) {
            return;
          }
          // 校验科目和巡检项
          if (!this.formData.subjects || this.formData.subjects.length === 0) {
            this.$message.warning('请至少添加一个巡检科目');
            return;
          }
          // 校验每个科目名称和巡检项内容
          for (let s = 0; s < this.formData.subjects.length; s++) {
            const subject = this.formData.subjects[s];
            if (!subject.subjectName || !subject.subjectName.trim()) {
              this.$message.warning(`请填写巡检科目${s + 1}的科目名称`);
              return;
            }
            if (!subject.items || subject.items.length === 0) {
              this.$message.warning(`巡检科目${s + 1}下至少需要添加一个巡检项`);
              return;
            }
            for (let i = 0; i < subject.items.length; i++) {
              const item = subject.items[i];
              if (!item.inspectionContent || !item.inspectionContent.trim()) {
                this.$message.warning(`请填写巡检科目${s + 1}下巡检项${i + 1}的巡检内容`);
                return;
              }
              // 校验操作项至少选一个
              if (!item.operationOptionList || item.operationOptionList.length === 0) {
                this.$message.warning(`请为巡检科目${s + 1}下巡检项${i + 1}选择操作项`);
                return;
              }
            }
          }
          // 构建保存参数
          const saveDTO = this.buildSaveDTO();
          inspectionConfigApi.save(saveDTO).then(() => {
            this.$message.success('保存成功');
            this.isEdit = false;
            this.getConfig();
          });
        });
      },
      /** 构建保存参数 */
      buildSaveDTO() {
        const data = JSON.parse(JSON.stringify(this.formData));
        // 将 operationOptionList 转为逗号分隔字符串
        if (data.subjects) {
          data.subjects.forEach(subject => {
            if (subject.items) {
              subject.items.forEach(item => {
                if (item.operationOptionList && item.operationOptionList.length > 0) {
                  item.operationOptions = item.operationOptionList.join(',');
                }
                delete item.operationOptionList;
              });
            }
          });
        }
        return data;
      },
      /** 新增巡检科目 */
      addSubject() {
        if (!this.formData.subjects) {
          this.formData.subjects = [];
        }
        this.formData.subjects.push({
          subjectName: '',
          sort: (this.formData.subjects.length + 1) * 100,
          items: [this.createEmptyItem()],
        });
        // 自动展开新增的科目
        this.activeSubject = String(this.formData.subjects.length - 1);
      },
      /** 删除巡检科目 */
      removeSubject(index) {
        this.formData.subjects.splice(index, 1);
        // 展开第一个科目
        if (this.formData.subjects.length > 0) {
          this.activeSubject = '0';
        }
      },
      /** 新增巡检项 */
      addItem(subjectIndex) {
        this.formData.subjects[subjectIndex].items.push(this.createEmptyItem());
      },
      /** 删除巡检项 */
      removeItem(subjectIndex, itemIndex) {
        this.formData.subjects[subjectIndex].items.splice(itemIndex, 1);
      },
      /** 创建空巡检项 */
      createEmptyItem() {
        return {
          inspectionContent: '',
          inspectionStandard: '',
          operationOptionList: [1, 2],
          needUploadPhoto: 1,
          needWriteRecord: 1,
          sort: 1,
        };
      },
    },
  };
</script>

<style lang="scss" scoped>
  .inspection-config {
    .config-container {
      padding: 16px 24px;

      .config-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 20px;

        .config-title {
          font-size: 18px;
          font-weight: 600;
          color: #333;
        }
      }

      .config-body {
        .config-section {
          margin-bottom: 32px;

          .section-title {
            display: flex;
            justify-content: space-between;
            align-items: center;
            font-size: 15px;
            font-weight: 600;
            color: #333;
            margin-bottom: 16px;
            padding-bottom: 8px;
            border-bottom: 1px solid #ebeef5;
          }

          .unit-text {
            margin-left: 8px;
            color: #909399;
          }
        }
      }

      ::v-deep .el-collapse {
        border: none;

        .el-collapse-item {
          background: #fafafa;
          border: 1px solid #ebeef5;
          border-radius: 8px;
          margin-bottom: 20px;
          overflow: hidden;

          .el-collapse-item__header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 0 20px;
            border-bottom: 1px solid #ebeef5;
            font-size: 14px;
            font-weight: 600;
            color: #606266;
            background: #fafafa;

            .subject-header {
              display: flex;
              justify-content: space-between;
              align-items: center;
              width: 100%;
              padding-right: 12px;

              .subject-label {
                font-size: 14px;
                font-weight: 600;
                color: #606266;
              }

              .delete-btn {
                color: #f56c6c;
              }
            }
          }

          .el-collapse-item__wrap {
            border: none;

            .el-collapse-item__content {
              padding: 20px;

              .item-card {
                background: #fff;
                border: 1px solid #ebeef5;
                border-radius: 6px;
                padding: 16px;
                margin-bottom: 12px;

                .item-header {
                  display: flex;
                  justify-content: space-between;
                  align-items: center;
                  margin-bottom: 12px;

                  .item-label {
                    font-size: 13px;
                    font-weight: 600;
                    color: #909399;
                  }

                  .delete-btn {
                    color: #f56c6c;
                  }
                }
              }

              .add-item-btn {
                text-align: center;
                padding: 8px 0;
              }
            }
          }
        }
      }

      .empty-tip {
        text-align: center;
        padding: 40px 0;
        color: #909399;
        font-size: 14px;
      }

      .config-footer {
        display: flex;
        justify-content: center;
        gap: 12px;
        padding: 20px 0;
        border-top: 1px solid #ebeef5;
      }
    }
  }
</style>
